"use client";

import { AlertTriangle, Info, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { t, type Locale } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import UploadFile, {
    FileType,
    type UploadFileType,
} from "@/components/utils/uploadFile";
import {
    DIGITAL_TWIN_VIDEO,
    REFERENCE_IMAGE,
    validateDigitalTwinVideo,
} from "../../lib/videoRequirements";
import type { PendingReferenceImage, WizardState } from "./types";

interface Step2Props {
    state: WizardState;
    update: (patch: Partial<WizardState>) => void;
}

const PHOTO_UPLOAD_PATH = "avatars/heygen/source/photo";
const VIDEO_UPLOAD_PATH = "avatars/heygen/source/video";
const REF_UPLOAD_PATH = "avatars/heygen/source/prompt-refs";

export default function Step2ProvideMedia({ state, update }: Step2Props) {
    if (state.mode === "photo")
        return <PhotoStep state={state} update={update} />;
    if (state.mode === "video")
        return <VideoStep state={state} update={update} />;
    if (state.mode === "prompt")
        return <PromptStep state={state} update={update} />;
    return null;
}

function HintRow({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <Info className="size-3.5 mt-0.5 flex-none" />
            <span className="leading-relaxed">{children}</span>
        </div>
    );
}

function PhotoStep({ state, update }: Step2Props) {
    const { locale } = useLocale();
    return (
        <div className="flex flex-col gap-3">
            <UploadFile
                options={{
                    allowedFileTypes: [FileType.Images],
                    maxFileSize: 32,
                    fileUploadPath: PHOTO_UPLOAD_PATH,
                    showImagePreview: true,
                    className: "h-48 w-full",
                    title: {
                        text: t("avatars.wizard.step2.photo.upload", locale),
                        keyName: "avatars.wizard.step2.photo.upload",
                    },
                }}
                initialFile={
                    state.url
                        ? { fileUrl: state.url, fileName: "portrait" }
                        : null
                }
                onUploadFinished={(uploaded: UploadFileType) => {
                    update({
                        url: uploaded.fileUrl,
                        assetId: undefined,
                        sourcePreviewUrl: uploaded.fileUrl,
                    });
                }}
            />
        </div>
    );
}

function localizeVideoValidationError(
    result: ReturnType<typeof validateDigitalTwinVideo>,
    locale: Locale,
    durationSec: number,
    mimeType: string
): { error?: string; warning?: string } {
    if (result.ok) {
        if (result.warning) {
            return {
                warning: t("avatars.wizard.step2.video.warning.short", locale, {
                    min: DIGITAL_TWIN_VIDEO.recommendedMinSeconds,
                }),
            };
        }
        return {};
    }
    // Match the same conditions as validateDigitalTwinVideo so we can localize.
    if (
        !DIGITAL_TWIN_VIDEO.supportedMimeTypes.includes(
            mimeType as (typeof DIGITAL_TWIN_VIDEO.supportedMimeTypes)[number]
        )
    ) {
        return {
            error: t("avatars.wizard.step2.video.error.format", locale, {
                type: mimeType,
            }),
        };
    }
    if (durationSec < DIGITAL_TWIN_VIDEO.hardMinSeconds) {
        return {
            error: t("avatars.wizard.step2.video.error.too-short", locale, {
                seconds: durationSec.toFixed(1),
                min: DIGITAL_TWIN_VIDEO.hardMinSeconds,
            }),
        };
    }
    if (durationSec > DIGITAL_TWIN_VIDEO.hardMaxSeconds) {
        return {
            error: t("avatars.wizard.step2.video.error.too-long", locale, {
                seconds: durationSec.toFixed(0),
                max: DIGITAL_TWIN_VIDEO.hardMaxSeconds,
            }),
        };
    }
    return {
        error: t("avatars.wizard.step2.video.error.too-large", locale),
    };
}

function VideoStep({ state, update }: Step2Props) {
    const { locale } = useLocale();

    const probeDuration = (url: string) =>
        new Promise<number>((resolve, reject) => {
            const v = document.createElement("video");
            v.preload = "metadata";
            v.onloadedmetadata = () => resolve(v.duration);
            v.onerror = () =>
                reject(
                    new Error(
                        t("avatars.wizard.step2.video.read-error", locale)
                    )
                );
            v.src = url;
        });

    const handleUploaded = async (uploaded: UploadFileType) => {
        update({ mediaError: undefined, mediaWarning: undefined });

        let duration = 0;
        try {
            duration = await probeDuration(uploaded.fileUrl);
        } catch {
            // Duration probe is a UX nicety; tolerate failures (some CORS
            // configs strip Range requests that videos need to read metadata)
        }

        if (duration > 0) {
            const result = validateDigitalTwinVideo(
                uploaded.fileSize ?? 0,
                duration,
                uploaded.fileType ?? "video/mp4"
            );
            const { error, warning } = localizeVideoValidationError(
                result,
                locale,
                duration,
                uploaded.fileType ?? "video/mp4"
            );
            if (error) {
                update({ mediaError: error });
                return;
            }
            if (warning) update({ mediaWarning: warning });
        }

        update({
            url: uploaded.fileUrl,
            assetId: undefined,
            sourcePreviewUrl: uploaded.fileUrl,
            videoDurationSec: duration > 0 ? duration : undefined,
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <UploadFile
                options={{
                    allowedFileTypes: [FileType.Video],
                    maxFileSize: 100,
                    fileUploadPath: VIDEO_UPLOAD_PATH,
                    className: "h-48 w-full",
                    title: {
                        text: t("avatars.wizard.step2.video.upload", locale),
                        keyName: "avatars.wizard.step2.video.upload",
                    },
                }}
                initialFile={
                    state.url
                        ? { fileUrl: state.url, fileName: "training-video" }
                        : null
                }
                onUploadFinished={handleUploaded}
            />
            {state.mediaWarning && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                    <AlertTriangle className="size-3.5 mt-0.5 flex-none" />
                    <span className="leading-relaxed">
                        {state.mediaWarning}
                    </span>
                </div>
            )}
            {state.mediaError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    <AlertTriangle className="size-3.5 mt-0.5 flex-none" />
                    <span className="leading-relaxed">{state.mediaError}</span>
                </div>
            )}
            <HintRow>
                {t("avatars.wizard.step2.video.hint", locale, {
                    min: DIGITAL_TWIN_VIDEO.recommendedMinSeconds,
                    max: DIGITAL_TWIN_VIDEO.recommendedMaxSeconds,
                })}
            </HintRow>
        </div>
    );
}

function PromptStep({ state, update }: Step2Props) {
    const { locale } = useLocale();
    const canAddMore = state.referenceImages.length < REFERENCE_IMAGE.maxCount;

    const handleAddReference = (uploaded: UploadFileType) => {
        const next: PendingReferenceImage = {
            url: uploaded.fileUrl,
            previewUrl: uploaded.fileUrl,
            fileName: uploaded.fileName,
        };
        update({ referenceImages: [...state.referenceImages, next] });
    };

    const handleRemoveReference = (idx: number) => {
        const next = [...state.referenceImages];
        next.splice(idx, 1);
        update({ referenceImages: next });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">
                    {t("avatars.wizard.step2.prompt.describe", locale)}
                </label>
                <Textarea
                    value={state.prompt}
                    onChange={(e) => update({ prompt: e.target.value })}
                    maxLength={1000}
                    rows={5}
                    placeholder={t(
                        "avatars.wizard.step2.prompt.placeholder",
                        locale
                    )}
                />
                <span className="text-xs text-muted-foreground tabular-nums self-end">
                    {state.prompt.length}/1000
                </span>
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                    <label className="text-sm font-medium">
                        {t("avatars.wizard.step2.prompt.references", locale, {
                            max: REFERENCE_IMAGE.maxCount,
                        })}
                    </label>
                    <span className="text-xs text-muted-foreground">
                        {state.referenceImages.length}/
                        {REFERENCE_IMAGE.maxCount}
                    </span>
                </div>
                {canAddMore && (
                    <UploadFile
                        options={{
                            allowedFileTypes: [FileType.Images],
                            maxFileSize: 32,
                            fileUploadPath: REF_UPLOAD_PATH,
                            className: "h-24 w-full",
                            title: {
                                text: t(
                                    "avatars.wizard.step2.prompt.refs.add",
                                    locale
                                ),
                                keyName: "avatars.wizard.step2.prompt.refs.add",
                            },
                            subtitle: {
                                text: t(
                                    "avatars.wizard.step2.prompt.refs.subtitle",
                                    locale
                                ),
                                keyName:
                                    "avatars.wizard.step2.prompt.refs.subtitle",
                            },
                        }}
                        onUploadFinished={handleAddReference}
                    />
                )}
                {state.referenceImages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {state.referenceImages.map((img, idx) => (
                            <div
                                key={idx}
                                className="group relative h-20 w-20 rounded-md overflow-hidden border bg-muted"
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={img.previewUrl}
                                    alt={img.fileName}
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemoveReference(idx)}
                                    aria-label={t(
                                        "avatars.wizard.step2.prompt.refs.remove",
                                        locale
                                    )}
                                    title={t(
                                        "avatars.wizard.step2.prompt.refs.remove",
                                        locale
                                    )}
                                    className="absolute top-1 end-1 inline-flex items-center justify-center size-5 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-black/75 backdrop-blur-sm"
                                >
                                    <X className="size-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
